import { useState } from "react";
import { ShareNetwork, Copy, Check, X, WhatsappLogo, FacebookLogo, XLogo, LinkedinLogo, EnvelopeSimple } from "@phosphor-icons/react";

// ITER 73 — one-click sharing (world-practice pattern used by NYT/Bloomberg/
// Substack): on mobile, `navigator.share()` opens the OS's own native share
// sheet — WhatsApp/Messages/Mail/whatever's installed — with the link
// pre-filled, zero copy-pasting. Where that API isn't available (most
// desktop browsers), a fallback modal offers direct one-tap deep-links into
// WhatsApp/Facebook/X/LinkedIn/Email (each pre-fills the message + link —
// the target app opens ready to send) plus a manual copy-link as a last
// resort. Either way, whoever receives the link still gets a rich preview
// card because `url` should always point at a `/api/share/...` OG-tag
// endpoint (see articles.py / public.py `share_*_og`), never the bare SPA
// route directly.
export function ShareButton({ url, title, text, lang = "en", testidPrefix = "share" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const shareText = text || title || "";

  const handleClick = async () => {
    if (navigator.share) {
      try {
        // ITER 76 — `url` ONLY. Chrome's Web Share implementation on
        // Android concatenates whichever of {title, text, url} are present
        // into a single message body for share targets that only accept
        // plain text (WhatsApp is exactly that kind of target) — so even
        // dropping just `text` (ITER 75) still left `title` to be merged
        // with `url` into a duplicate-looking message. Passing url alone
        // leaves nothing for it to concatenate; WhatsApp's own OG-tag-based
        // preview card still shows the title/image regardless.
        await navigator.share({ url });
        return; // handled natively — never show the fallback modal
      } catch (e) {
        if (e?.name === "AbortError") return; // user cancelled the native sheet, don't fall back
      }
    }
    setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      /* clipboard API unavailable — user can still select+copy the text field */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const enc = encodeURIComponent;
  const links = [
    // WhatsApp: URL only, no caption text. WhatsApp auto-generates a rich
    // preview card (image+title+description) from the link's OG tags —
    // pre-filling the title as message text too just duplicates it below
    // the card (see the reported bug: title shown once in the card, then
    // again as plain text, then the raw URL a third time).
    { name: "WhatsApp", icon: WhatsappLogo, href: `https://wa.me/?text=${enc(url)}`, bg: "#25D366" },
    { name: "Facebook", icon: FacebookLogo, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`, bg: "#1877F2" },
    { name: "X", icon: XLogo, href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(shareText)}`, bg: "#000000" },
    { name: "LinkedIn", icon: LinkedinLogo, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`, bg: "#0A66C2" },
    { name: "Email", icon: EnvelopeSimple, href: `mailto:?subject=${enc(title || "")}&body=${enc(`${shareText}\n\n${url}`)}`, bg: "#444444" },
  ];

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid={`${testidPrefix}-button`}
        className="inline-flex items-center gap-1.5 border-2 border-black px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-mango hover:text-white transition-colors"
      >
        <ShareNetwork size={14} /> {lang === "ur" ? "شیئر" : "Share"}
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid={`${testidPrefix}-modal`}>
          <div className="bg-white border-2 border-black w-full max-w-md hard-shadow" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b-2 border-black">
              <div className="overline">{lang === "ur" ? "شیئر کریں" : "SHARE"}</div>
              <button onClick={() => setOpen(false)} data-testid={`${testidPrefix}-close`} className="text-neutral-500 hover:text-black"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-5 gap-3">
                {links.map((l) => (
                  <a
                    key={l.name}
                    href={l.href}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`${testidPrefix}-${l.name.toLowerCase()}`}
                    className="flex flex-col items-center gap-1.5 group"
                  >
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center border-2 border-black group-hover:scale-110 transition-transform"
                      style={{ background: l.bg }}
                    >
                      <l.icon size={20} color="#fff" weight="fill" />
                    </div>
                    <span className="text-[10px] font-bold uppercase text-neutral-600">{l.name}</span>
                  </a>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={url}
                  data-testid={`${testidPrefix}-url`}
                  onFocus={(e) => e.target.select()}
                  dir="ltr"
                  className="flex-1 border-2 border-black px-3 py-2 text-sm font-mono bg-neutral-50 min-w-0"
                />
                <button
                  type="button"
                  onClick={copy}
                  data-testid={`${testidPrefix}-copy`}
                  className={`shrink-0 px-4 py-2 text-sm font-bold border-2 border-black inline-flex items-center gap-1.5 ${copied ? "bg-emerald-600 text-white" : "bg-black text-white hover:bg-mango"}`}
                >
                  {copied ? <><Check size={14} /> {lang === "ur" ? "کاپی ہو گیا" : "Copied"}</> : <><Copy size={14} /> {lang === "ur" ? "کاپی" : "Copy"}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
